/**
 * MapOverlay Component
 *
 * Displays store information overlay on the left half of the map.
 * Shows address, directions button, wheelchair access, and parking info.
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Wheelchair icon component
 */
const WheelchairIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm7 18h-2.5l-1.5-5h-4v-2h5l1.5 5H19v2zm-7-8h-3V9.5a1.5 1.5 0 0 1 3 0V12zm-2 8a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
      fill="currentColor"
    />
  </Icon>
);

/**
 * Parking icon component (P in circle)
 */
const ParkingIcon = (props) => (
  <Box
    alignItems="center"
    bg="gray.600"
    borderRadius="full"
    display="flex"
    flexShrink={0}
    h={5}
    justifyContent="center"
    w={5}
    {...props}
  >
    <Text color="white" fontSize="xs" fontWeight="bold" lineHeight="1">
      P
    </Text>
  </Box>
);

/**
 * MapOverlay component
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - SFCC Store data
 * @param {boolean} props.wheelchairAccessible - Wheelchair accessible flag from Contentful
 * @param {string} props.parkingAddress - Parking address from Contentful
 * @returns {React.ReactElement}
 */
export const MapOverlay = ({
  parkingAddress = '',
  store,
  wheelchairAccessible = false,
  ...props
}) => {
  if (!store) {
    return null;
  }

  // Build full address string
  const fullAddress = [
    store.address1,
    store.address2,
    store.city,
    store.stateCode,
    store.postalCode
  ].filter(Boolean).join(', ');

  // Handle get directions click - opens Google Maps directions
  const handleGetDirections = () => {
    const destination = encodeURIComponent(fullAddress);
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const hasParkingInfo = !!parkingAddress;
  const hasAccessibilityInfo = wheelchairAccessible || hasParkingInfo;

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="lg"
      left={4}
      maxW="33%"
      p={5}
      position="absolute"
      top="50%"
      transform="translateY(-50%)"
      zIndex={10}
      {...props}
    >
      <VStack align="stretch" spacing={4}>
        {/* Address Header */}
        <Text
          color="gray.800"
          fontSize="sm"
          fontWeight="medium"
          lineHeight="short"
        >
          {fullAddress}
        </Text>

        {/* Get Direction Button */}
        <Button
          _hover={{bg: 'gray.800'}}
          bg="black"
          color="white"
          fontWeight="medium"
          size="sm"
          w="full"
          onClick={handleGetDirections}
        >
          Get direction
        </Button>

        {/* Accessibility & Parking Info */}
        {hasAccessibilityInfo && (
          <VStack align="stretch" pt={2} spacing={3}>
            {/* Wheelchair Access */}
            {wheelchairAccessible && (
              <HStack spacing={3}>
                <WheelchairIcon boxSize={5} color="gray.600" />
                <Text color="gray.700" fontSize="sm">
                  Wheelchair access
                </Text>
              </HStack>
            )}

            {/* Parking Info */}
            {hasParkingInfo && (
              <VStack align="stretch" spacing={1}>
                <HStack spacing={3}>
                  <ParkingIcon boxSize={5} color="gray.600" />
                  <Text color="gray.700" fontSize="sm">
                    Carpark available
                  </Text>
                </HStack>
                <Text color="gray.500" fontSize="xs" pl={8}>
                  {parkingAddress}
                </Text>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

MapOverlay.displayName = 'MapOverlay';

MapOverlay.propTypes = {
  parkingAddress: PropTypes.string,
  store: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    stateCode: PropTypes.string,
  }),
  wheelchairAccessible: PropTypes.bool,
};

export default MapOverlay;
